import type { LessonModel } from '../Models/LessonModel';
import {
  buildLessonEvent,
  buildSlotEvent,
  type CalendarEvent,
  type SlotEventInput
} from '../Utils/GoogleCalendarSync';

/**
 * Google Calendar, through Google Identity Services in the browser.
 *
 * The access token never reaches the Magbit API: the calendar belongs to the
 * tutor's Google account, not to us, so the browser holds a short-lived token
 * and talks to Google directly. All Magbit stores is the event id, so it can
 * find the event again.
 *
 * The client id and the scope are publishable; the scope is deliberately
 * `calendar.events` and not full calendar access, so this can never read or
 * change anything but the events it created.
 */

const CLIENT_ID = '533169606964-mgvn99cbhqgvv3mm5h768u440kvj266b.apps.googleusercontent.com';
const SCOPE = 'https://www.googleapis.com/auth/calendar.events';
const CALENDAR_API = 'https://www.googleapis.com/calendar/v3';
const PRIMARY = 'primary';
const LAST_TIME_KEY = 'magbit.lastLessonTime';
/** That the scope was granted in this browser, never the token itself. */
const GRANTED_KEY = 'magbit.gcalGranted';
const EMAIL_KEY = 'magbit.gcalEmail';

interface TokenResponse {
  access_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

interface TokenClient {
  callback: (response: TokenResponse) => void;
  requestAccessToken(options?: { prompt?: string }): void;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient(config: {
            client_id: string;
            scope: string;
            callback: (response: TokenResponse) => void;
          }): TokenClient;
          revoke(token: string, done: () => void): void;
        };
      };
    };
  }
}

interface State {
  scriptPromise: Promise<void> | null;
  tokenClient: TokenClient | null;
  accessToken: string | null;
  expiresAt: number;
  email: string | null;
  listeners: Set<(status: { connected: boolean; email: string | null }) => void>;
}

const state: State = {
  scriptPromise: null,
  tokenClient: null,
  accessToken: null,
  expiresAt: 0,
  email: null,
  listeners: new Set()
};

function notify(): void {
  const snapshot = { connected: isConnected(), email: state.email };
  for (const listener of state.listeners) {
    try {
      listener(snapshot);
    } catch (error) {
      console.error('[gcal] listener failed', error);
    }
  }
}

/**
 * Loaded on demand rather than in index.html: a tutor who never connects a
 * calendar should not pay for Google's script on every page load.
 */
function loadIdentityScript(): Promise<void> {
  if (state.scriptPromise) return state.scriptPromise;
  state.scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load Google Identity Services'));
    document.head.appendChild(script);
  });
  return state.scriptPromise;
}

async function ensureTokenClient(): Promise<TokenClient> {
  await loadIdentityScript();
  const oauth2 = window.google?.accounts?.oauth2;
  if (!oauth2) throw new Error('Google Identity Services unavailable');
  state.tokenClient ??= oauth2.initTokenClient({
    client_id: CLIENT_ID,
    scope: SCOPE,
    // Replaced per request below; the client insists on one at construction.
    callback: () => {}
  });
  return state.tokenClient;
}

function requestAccessToken(prompt: string): Promise<string> {
  return new Promise((resolve, reject) => {
    ensureTokenClient().then(client => {
      client.callback = (response: TokenResponse) => {
        if (response.error || !response.access_token) {
          reject(new Error(response.error_description ?? response.error ?? 'No token'));
          return;
        }
        state.accessToken = response.access_token;
        state.expiresAt = Date.now() + Number(response.expires_in ?? 3600) * 1000;
        resolve(response.access_token);
        notify();
      };
      client.requestAccessToken({ prompt });
    }).catch(reject);
  });
}

function isConnected(): boolean {
  return Boolean(state.accessToken) && state.expiresAt > Date.now() + 5000;
}

/**
 * A silent refresh first, and a consent prompt only when the caller is
 * prepared to interrupt the tutor. Background sync must never open a popup.
 */
async function validToken(interactive: boolean): Promise<string> {
  if (state.accessToken && state.expiresAt > Date.now() + 10_000) return state.accessToken;
  try {
    return await requestAccessToken('');
  } catch (error) {
    if (!interactive) throw error;
    return requestAccessToken('consent');
  }
}

/**
 * Carries the HTTP status, because callers need to tell "already gone" from
 * "failed". Google answers a missing event with the message "Not Found" and a
 * deleted one with 410, so matching on message text gets both wrong.
 */
export class CalendarApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'CalendarApiError';
  }
}

async function api<T>(path: string, options: RequestInit = {}): Promise<T | null> {
  const token = await validToken(false);
  const response = await fetch(`${CALENDAR_API}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    }
  });
  if (response.status === 204) return null;
  const text = await response.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (!response.ok) {
    const message = (data as { error?: { message?: string } })?.error?.message
      ?? `Calendar API ${response.status}`;
    throw new CalendarApiError(message, response.status);
  }
  return data as T;
}

/**
 * A local calendar day as the UTC instant it actually starts at.
 *
 * `${date}T00:00:00Z` is not midnight anywhere east of Greenwich: in Jerusalem
 * it is three in the morning, so a window built that way misses the early
 * hours of its own first day. Constructing the Date from local parts and
 * letting it convert gets the real instant.
 */
function dayStartIso(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1, 0, 0, 0, 0).toISOString();
}

function dayEndIso(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1, 23, 59, 59, 999).toISOString();
}

/**
 * The default hour for a lesson with no time of its own: the last hour the
 * tutor actually used, so it follows their schedule rather than a constant.
 * Keeping events timed rather than all-day is what makes the calendar useful.
 */
export function defaultLessonTime(): string {
  const stored = localStorage.getItem(LAST_TIME_KEY);
  return stored && /^\d{2}:\d{2}$/.test(stored) ? stored : '16:00';
}

export function rememberLessonTime(time: string | null): void {
  if (time && /^\d{2}:\d{2}$/.test(time)) localStorage.setItem(LAST_TIME_KEY, time);
}

function localTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Jerusalem';
}

function lessonEventBody(lesson: LessonModel, userId: string): CalendarEvent {
  return buildLessonEvent(lesson, {
    userId,
    timeZone: localTimeZone(),
    defaultTime: defaultLessonTime()
  });
}

export const GoogleCalendarService = {
  isConnected,

  onChange(listener: (status: { connected: boolean; email: string | null }) => void): () => void {
    state.listeners.add(listener);
    listener({ connected: isConnected(), email: state.email });
    return () => state.listeners.delete(listener);
  },

  /**
   * True when a usable token was obtained without prompting.
   *
   * The access token is deliberately never persisted: it is a bearer
   * credential, and localStorage is readable by any script that gets onto the
   * page. What is remembered instead is only that this browser has granted
   * the scope before, which is enough to ask Google for a new token silently.
   * That is also what makes background sync possible at all, since it must
   * never open a popup.
   */
  async ensureSilentToken(): Promise<boolean> {
    if (isConnected()) return true;
    if (localStorage.getItem(GRANTED_KEY) !== '1') return false;
    try {
      await validToken(false);
      if (isConnected()) {
        state.email ??= localStorage.getItem(EMAIL_KEY);
        notify();
      }
      return isConnected();
    } catch {
      return false;
    }
  },

  async connect(): Promise<string | null> {
    const token = await requestAccessToken('consent');
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${token}` }
      });
      state.email = response.ok ? ((await response.json()) as { email?: string }).email ?? null : null;
    } catch {
      state.email = null;
    }
    localStorage.setItem(GRANTED_KEY, '1');
    if (state.email) localStorage.setItem(EMAIL_KEY, state.email);
    notify();
    return state.email;
  },

  disconnect(): void {
    const token = state.accessToken;
    // Revoking at Google as well as forgetting locally: "disconnect" should
    // mean the grant is gone, not that this tab stopped using it.
    if (token) {
      try {
        window.google?.accounts?.oauth2?.revoke(token, () => {});
      } catch {
        // Revocation is best effort; the local token is dropped either way.
      }
    }
    state.accessToken = null;
    state.expiresAt = 0;
    state.email = null;
    // Forgetting the grant too, or the next load would silently reconnect the
    // calendar the tutor just disconnected.
    localStorage.removeItem(GRANTED_KEY);
    localStorage.removeItem(EMAIL_KEY);
    notify();
  },

  async createLessonEvent(lesson: LessonModel, userId: string): Promise<string | null> {
    rememberLessonTime(lesson.time);
    const created = await api<{ id: string }>(`/calendars/${PRIMARY}/events`, {
      method: 'POST',
      body: JSON.stringify(lessonEventBody(lesson, userId))
    });
    return created?.id ?? null;
  },

  async updateLessonEvent(eventId: string, lesson: LessonModel, userId: string): Promise<void> {
    rememberLessonTime(lesson.time);
    await api(`/calendars/${PRIMARY}/events/${encodeURIComponent(eventId)}`, {
      method: 'PATCH',
      body: JSON.stringify(lessonEventBody(lesson, userId))
    });
  },

  async deleteEvent(eventId: string): Promise<void> {
    try {
      await api(`/calendars/${PRIMARY}/events/${encodeURIComponent(eventId)}`, { method: 'DELETE' });
    } catch (error) {
      // An event the tutor already removed in Google is the desired end state.
      // 404 is never-heard-of, 410 is deleted; both mean there is nothing left
      // to do, and neither is worth failing a lesson deletion over.
      const status = error instanceof CalendarApiError ? error.status : 0;
      if (status !== 404 && status !== 410) throw error;
    }
  },

  /** One event by id, or null when it is gone. */
  async getEvent(eventId: string): Promise<CalendarEvent | null> {
    try {
      const event = await api<CalendarEvent>(
        `/calendars/${PRIMARY}/events/${encodeURIComponent(eventId)}`
      );
      // A deleted event answers 200 with status `cancelled` rather than 404.
      return event && event.status !== 'cancelled' ? event : null;
    } catch (error) {
      const status = error instanceof CalendarApiError ? error.status : 0;
      if (status === 404 || status === 410) return null;
      throw error;
    }
  },

  async createSlotEvent(slot: SlotEventInput, userId: string): Promise<string | null> {
    const created = await api<{ id: string }>(`/calendars/${PRIMARY}/events`, {
      method: 'POST',
      body: JSON.stringify(buildSlotEvent(slot, { userId, timeZone: localTimeZone() }))
    });
    return created?.id ?? null;
  },

  async updateSlotEvent(eventId: string, slot: SlotEventInput, userId: string): Promise<void> {
    await api(`/calendars/${PRIMARY}/events/${encodeURIComponent(eventId)}`, {
      method: 'PATCH',
      body: JSON.stringify(buildSlotEvent(slot, { userId, timeZone: localTimeZone() }))
    });
  },

  /**
   * Every Magbit-managed event in a window, for a pull.
   *
   * `singleEvents` is left off so a recurring slot comes back as its master
   * event, carrying the id Magbit stored and its RRULE. Expanded instances
   * have generated ids that match nothing on this side.
   */
  async listMagbitEvents(from: string, to: string): Promise<CalendarEvent[]> {
    const params = new URLSearchParams({
      timeMin: dayStartIso(from),
      timeMax: dayEndIso(to),
      privateExtendedProperty: 'magbit=1',
      maxResults: '2500'
    });
    const page = await api<{ items?: CalendarEvent[] }>(
      `/calendars/${PRIMARY}/events?${params.toString()}`
    );
    return page?.items ?? [];
  }
};
